import React, {
    Children,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import {
    CancelModal,
    ConfirmationModal,
    HelixLoader,
    LandingPageWrapperWithLeftTabs,
} from 'components';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Box, Grid, Stack, Typography } from '@mui/material';
import {
    CreatePipelineDefaults,
    CreatePipelineFormData,
    CreatePipelineSchema,
    DETAILS,
    updatePipelineStyles,
    STEPS,
    TabItems,
    TabsItemsType,
    unsavedChangesConfirmationText,
    checkForDuplicatePipeline,
    DETAILS_TAB_INDEX,
    STEPS_TAB_INDEX,
    ListItemType,
    STEP_FIELDS,
} from './create-or-update-pipeline-helper';
import { AddStepModal, PipelineForm } from './components';
import {
    ConfirmationModalConfig,
    DynamicMenuItem,
} from 'components/landing-page-wrapper-with-left-tabs/components/HeaderWithActionButtons';
import { PIPELINES_AND_STEPS_PATHS } from 'constants/pipelines-and-steps-paths';
import {
    useAlert,
    useAuth,
    usePermission,
    useServicesEnabled,
} from 'core/contexts';
import {
    StepListResponse,
    useDeletePipeline,
    usePipeline,
    usePipelinesList,
    useStepsList,
    useUpdatePipeline,
} from 'hooks';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { StepOutlinedIcon } from 'assets/images';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { SaveModalProps } from 'components/save-modal/save-modal-helper';
import { commonShowAlert, ellipsifyTextInTwoLines } from 'lib';
import { AddStepFormData } from './components/add-step-modal-helper';
import { DraggableList, Tooltip } from '@biq/apex-ui';
import { attributesComponentStyles as draggableListStyles } from 'pages/dictionaries/components/drawers/attributes-component-helper';
import { TabItemsType } from 'components/landing-page-wrapper/components/TabComponent';
import InvalidPermissionsDialog from 'components/invalid-permissions-dialog/InvalidPermissionsDialog';
import { styles as confirmationModalStyles } from 'components/confirmation-modal/confirmation-modal-helper';
import { Icon } from '@iconify/react';
import { RemoveMenuItem } from './components/RemoveMenuItem';
import { commonStyles } from '../pipelines-helper';
import { TrialRunModal } from '../components';

export const UpdatePipeline = (): React.JSX.Element => {
    const { getRoles, getToken } = useAuth();
    const { isServiceEnabledFor } = useServicesEnabled();
    const isDataPipelineServiceEnabled = isServiceEnabledFor('DATA_PIPELINE');
    const { hasPermissions } = usePermission();
    const navigate = useNavigate();
    const [selectedTab, setSelectedTab] = useState<TabsItemsType>(DETAILS);
    const [isChangesUnsaved, setIsChangesUnsaved] = useState<boolean>(false);
    const [openCancelModal, setOpenCancelModal] = useState<boolean>(false);
    const [openSaveModal, setOpenSaveModal] = useState<boolean>(false);
    const [openDeleteConfirmationModal, setOpenDeleteConfirmationModal] =
        useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const saveActionRef = useRef<
        'save' | 'saveAndClose' | 'saveAndOpenTrialRun' | null
    >(null);
    const [draggableListArr, setDraggableListArr] = useState<
        {
            id: string | number;
            item: string | React.JSX.Element;
        }[]
    >([]);
    const [hasOpenAddStepModal, setHasOpenAddStepModal] =
        useState<boolean>(false);
    const [displayPermissionDialog, setDisplayPermissionDialog] =
        useState(false);
    const [tabNavItems, setTabNavItems] = useState<TabItemsType[]>(TabItems);
    const [readOnly, setReadOnly] = useState<boolean>(false);
    const [openConfirmationRemoveStep, setOpenConfirmationRemoveStep] =
        useState<boolean>(false);
    const [stepIndexToRemove, setStepIndexToRemove] = useState<number | null>(
        null
    );
    const [openTrialRunModal, setOpenTrialRunModal] = useState<boolean | null>(
        null
    );
    const { id } = useParams();
    const {
        isLoading: isPipelineLoading,
        isFetched,
        isSuccess,
        data,
        refetch,
    } = usePipeline({ id: id as string, token: getToken() });
    const {
        handleSubmit,
        control,
        watch,
        reset,
        setValue,
        getValues,
        formState: { errors },
    } = useForm<CreatePipelineFormData>({
        mode: 'all',
        resolver: yupResolver(CreatePipelineSchema),
        defaultValues: CreatePipelineDefaults,
    });
    const { data: pipelines } = usePipelinesList(isDataPipelineServiceEnabled);
    const { mutate: updatePipeline } = useUpdatePipeline();
    const { mutate: deleteDataPipeline } = useDeletePipeline();
    const {
        data: stepsData,
        isLoading: isStepsLoading,
        refetch: refetchStepsList,
    } = useStepsList(isDataPipelineServiceEnabled);
    const { showAlert, hideAlert } = useAlert();
    const name = watch('name');

    const handleSetDraggableListArr: React.Dispatch<
        React.SetStateAction<ListItemType[]>
    > = (value): void => {
        setDraggableListArr(value);
        setIsChangesUnsaved(true);
    };

    const setEmptyStepState = (): void => {
        tabNavItems[STEPS_TAB_INDEX] = {
            ...tabNavItems[STEPS_TAB_INDEX],
            tabHeaderConfig: {
                headingText:
                    TabItems[STEPS_TAB_INDEX].tabHeaderConfig?.headingText ??
                    '',
            },
            icon: (
                <Badge badgeContent='' sx={updatePipelineStyles.badgeStyles} />
            ),
            isEmptyState: true,
            emptyStateConfig: {
                emptyStateIcon: StepOutlinedIcon,
                emptyStateBodyMessage: 'Add a step to get started.',
                customImageStyles: updatePipelineStyles.emptyStepsImageStyles,
                hasAddButton: true,
                addButtonLabel: 'Add Step',
                handleAddButtonClick: (): void => {
                    if (readOnly) {
                        setDisplayPermissionDialog(true);
                        return;
                    }
                    setHasOpenAddStepModal(true);
                },
            },
        };
        setTabNavItems([...tabNavItems]);
    };

    const handleRemoveStepConfirmed = (index: number): void => {
        setIsChangesUnsaved(true);
        const newDraggableListArr = [...draggableListArr];
        newDraggableListArr.splice(index, 1);
        setDraggableListArr(newDraggableListArr);
        setValue(
            'stepIds',
            newDraggableListArr.length > 0
                ? newDraggableListArr.map(
                      (step) => step.id.toString().split('_')[1]
                  )
                : []
        );
        setStepIndexToRemove(null);
    };

    const handleRemoveStep = (index: number): void => {
        setStepIndexToRemove(index);
        setOpenConfirmationRemoveStep(true);
    };

    const handleConfirmRemove = (): void => {
        if (stepIndexToRemove !== null) {
            handleRemoveStepConfirmed(stepIndexToRemove);
            setOpenConfirmationRemoveStep(false);
        }
    };

    const handleCancelRemove = (): void => {
        setOpenConfirmationRemoveStep(false);
        setStepIndexToRemove(null);
    };

    const renderMenuItem = useCallback(
        (index: number): React.JSX.Element => (
            <RemoveMenuItem index={index} handleRemoveStep={handleRemoveStep} />
        ),
        [handleRemoveStep]
    );

    const setStepListState = (): void => {
        tabNavItems[STEPS_TAB_INDEX] = {
            ...tabNavItems[STEPS_TAB_INDEX],
            isEmptyState: false,
            emptyStateConfig: undefined,
            icon: undefined,
            tabHeaderConfig: {
                headingText:
                    TabItems[STEPS_TAB_INDEX].tabHeaderConfig?.headingText ??
                    '',
                headerButtonsConfig: [
                    {
                        sx: {
                            ...commonStyles.addButtonStyles,
                            ...commonStyles.headerTabButtonStyles,
                            ...updatePipelineStyles.addStepHeaderButtonWidth,
                        },
                        icon: <AddIcon />,
                        label: 'Add Step',
                        onClick: (): void => {
                            if (readOnly) {
                                setDisplayPermissionDialog(true);
                                return;
                            }
                            setHasOpenAddStepModal(true);
                        },
                    },
                    {
                        sx: {
                            ...commonStyles.addButtonStyles,
                            ...commonStyles.headerTabButtonStyles,
                            ...updatePipelineStyles.trialRunHeaderButtonWidth,
                        },
                        icon: (
                            <Icon
                                icon={'mdi:checkbox-marked-circle-outline'}
                                style={commonStyles.trialRunButtonIcon}
                            />
                        ),
                        label: 'Trial Run',
                        onClick: (): void => {
                            if (readOnly) {
                                setDisplayPermissionDialog(true);
                                return;
                            }
                            if (isChangesUnsaved) {
                                setOpenTrialRunModal(false);
                                return;
                            }
                            setOpenTrialRunModal(true);
                        },
                    },
                ],
            },
            tabContent: (
                <Box sx={updatePipelineStyles.draggableListContainerStyles}>
                    <DraggableList
                        listItems={draggableListArr}
                        setListItems={handleSetDraggableListArr}
                        isListItemArrowEnabled={false}
                        menuItemsDirection='reverse'
                        isMenuArrowsEnabled={true}
                        menuItems={renderMenuItem}
                        customStyles={{
                            ...draggableListStyles.attributesDraggableListContainerStyles,
                            ...updatePipelineStyles.draggableListStyles,
                        }}
                    />
                </Box>
            ),
        };
        setTabNavItems([...tabNavItems]);
    };

    const handleStepTableData = (): void => {
        if (!stepsData?.length) return;
        const stepIds = getValues('stepIds');
        const draggableStepItems = stepIds
            .map((stepId: string, index: number) => {
                let stepFound = stepsData.find(
                    (step: StepListResponse) => step.id === stepId
                );
                if (stepFound) {
                    return {
                        id: `${index}_${stepFound.id}`,
                        item: (
                            <Grid
                                className='draggableItem'
                                sx={
                                    updatePipelineStyles.draggableItemContainerStyles
                                }
                            >
                                {Children.toArray(
                                    STEP_FIELDS.map(
                                        ({ key, label, width, tooltip }) => {
                                            return (
                                                <Grid
                                                    sx={
                                                        updatePipelineStyles.draggableItemStyles
                                                    }
                                                    width={width}
                                                >
                                                    <Grid
                                                        sx={
                                                            updatePipelineStyles.draggableItemLabelStyles
                                                        }
                                                    >
                                                        {label}:
                                                    </Grid>
                                                    {tooltip ? (
                                                        <Tooltip
                                                            arrow
                                                            title={
                                                                <Box
                                                                    sx={
                                                                        updatePipelineStyles.tooltipOverflow
                                                                    }
                                                                >
                                                                    {
                                                                        stepFound[
                                                                            key
                                                                        ]
                                                                    }
                                                                </Box>
                                                            }
                                                        >
                                                            <Grid
                                                                sx={{
                                                                    ...updatePipelineStyles.draggableItemValueStyles,
                                                                    ...ellipsifyTextInTwoLines,
                                                                }}
                                                            >
                                                                {stepFound[key]}
                                                            </Grid>
                                                        </Tooltip>
                                                    ) : (
                                                        <Grid
                                                            sx={{
                                                                ...updatePipelineStyles.draggableItemValueStyles,
                                                                ...ellipsifyTextInTwoLines,
                                                            }}
                                                        >
                                                            {stepFound[key]}
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            );
                                        }
                                    )
                                )}
                            </Grid>
                        ),
                    };
                }
                return undefined;
            })
            .filter(
                (item): item is { id: string; item: JSX.Element } =>
                    item !== undefined
            );
        setDraggableListArr(draggableStepItems);
    };

    useEffect(() => {
        if (getRoles().includes('sdzAdmin')) {
            setReadOnly(false);
        } else {
            setReadOnly(true);
        }
    }, []);

    useEffect(() => {
        if (draggableListArr.length) {
            setStepListState();
        } else {
            setEmptyStepState();
        }
    }, [draggableListArr]);

    useLayoutEffect(() => {
        tabNavItems[DETAILS_TAB_INDEX].tabContent = (
            <PipelineForm
                control={control}
                errors={errors}
                pipelineFormValues={data}
                isEdit={true}
                handleUnsavedChanges={(): void => {
                    setIsChangesUnsaved(true);
                }}
            />
        );
        setTabNavItems([...tabNavItems]);
        if (data?.stepIds && data.stepIds.length > 0) {
            handleStepTableData();
        } else {
            setEmptyStepState();
        }
    }, [data, stepsData]);

    useEffect(() => {
        if (data && isFetched && isSuccess) {
            reset({
                id: data.id,
                name: data.name,
                description: data.description,
                stepIds: data.stepIds,
            });
            refetchStepsList();

            if (!data.stepIds || data.stepIds.length === 0) {
                setEmptyStepState();
            } else {
                handleStepTableData();
            }
        } else if (!data) {
            reset(CreatePipelineDefaults);
            setEmptyStepState();
        }
    }, [data, isFetched, isSuccess]);

    useEffect(() => {
        if (name) {
            tabNavItems[DETAILS_TAB_INDEX].isError = false;
            setTabNavItems([...tabNavItems]);
        }
    }, [name]);

    const handleDeleteConfirmationModal = (): void => {
        const permission: boolean = hasPermissions(['deleteMetadata']);
        if (!permission) {
            setDisplayPermissionDialog(true);
            return;
        }
        setOpenDeleteConfirmationModal(true);
    };

    const menuItems: DynamicMenuItem[] = [
        {
            label: 'Delete Pipeline',
            icon: <DeleteIcon sx={commonStyles.deleteIconStyles} />,
            onClick: (): void => handleDeleteConfirmationModal(),
        },
    ];

    const onTabChange = (
        event: React.SyntheticEvent,
        newValue: TabsItemsType
    ): void => {
        if (event) {
            if (newValue === DETAILS) {
                setSelectedTab(newValue);
            } else if (newValue === STEPS) {
                setSelectedTab(newValue);
                if (!name) {
                    TabItems[DETAILS_TAB_INDEX].isError = true;
                }
                const stepIds = getValues('stepIds');
                if (stepIds && stepIds.length > 0) {
                    handleStepTableData();
                } else {
                    setEmptyStepState();
                }
            }
        }
    };

    const handleOpenModal = (modalType: 'cancel' | 'save' | 'delete'): void => {
        if (modalType === 'cancel') {
            setOpenCancelModal(false);
        } else if (modalType === 'save') {
            setOpenSaveModal(false);
        } else if (modalType === 'delete') {
            setOpenDeleteConfirmationModal(false);
        }
    };

    const handleCancelModalConfirmation = (): void => {
        setOpenCancelModal(false);
        tabNavItems[DETAILS_TAB_INDEX].isError = false;
        navigate(PIPELINES_AND_STEPS_PATHS.LANDING_PAGE);
    };

    const handleCancelButton = (): void => {
        if (isChangesUnsaved) {
            setOpenCancelModal(true);
        } else {
            tabNavItems[DETAILS_TAB_INDEX].isError = false;
            navigate(PIPELINES_AND_STEPS_PATHS.LANDING_PAGE);
        }
    };

    const onSubmit = (formValues: CreatePipelineFormData): void => {
        setOpenSaveModal(false);
        setIsLoading(true);
        let isDuplicatePipeline = false;
        isDuplicatePipeline = checkForDuplicatePipeline(
            pipelines?.data ?? [],
            formValues
        );
        if (isDuplicatePipeline) {
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                'A pipeline with this name already exists.'
            );
            setIsLoading(false);
            return;
        }
        updatePipeline(
            {
                token: getToken() as string,
                putData: {
                    ...formValues,
                    stepIds: draggableListArr.map(
                        (item) => item.id.toString().split('_')[1]
                    ),
                },
            },
            {
                onSuccess: (): void => {
                    tabNavItems[DETAILS_TAB_INDEX].isError = false;
                    setIsLoading(false);
                    setIsChangesUnsaved(false);
                    if (saveActionRef.current === 'saveAndClose') {
                        setTimeout(() => {
                            navigate(PIPELINES_AND_STEPS_PATHS.LANDING_PAGE);
                        }, 1000);
                    } else if (saveActionRef.current === 'save') {
                        refetch();
                    } else if (
                        saveActionRef.current === 'saveAndOpenTrialRun'
                    ) {
                        refetch();
                        setOpenTrialRunModal(true);
                    }
                    saveActionRef.current = null;
                },
                onError: (): void => {
                    saveActionRef.current = null;
                    setIsLoading(false);
                    setIsChangesUnsaved(true);
                },
            }
        );
    };

    const handleSaveModalButtonClick = (): void => {
        handleSubmit(onSubmit)();
    };

    const handleSaveButtonClick = (
        actionType: 'save' | 'saveAndClose' | 'saveAndOpenTrialRun'
    ): void => {
        const permission: boolean = hasPermissions(['editMetadata']);
        if (!permission) {
            setDisplayPermissionDialog(true);
            return;
        }
        saveActionRef.current = actionType;
        if (!name) {
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                'Pipelines must have a name.'
            );
        } else if (getValues('stepIds').length === 0) {
            setOpenSaveModal(true);
        } else {
            handleSaveModalButtonClick();
        }
    };

    const handleSaveModalConfig = (): SaveModalProps => {
        const saveModalBody: React.ReactNode = (
            <Stack spacing={1.5}>
                <Typography>
                    There are no steps defined, so saving it at this stage will
                    have no functional effect.
                </Typography>
                <Typography>
                    Are you sure you want to save an empty pipeline?
                </Typography>
            </Stack>
        );
        return {
            openSaveModal: openSaveModal,
            saveHeadingText: 'Your Pipeline is Currently Empty',
            saveButtonText: 'Save Anyway',
            handleSaveClick: (): void => handleSaveModalButtonClick(),
            cancelButtonText: 'Cancel',
            handleCancelClick: (): void => handleOpenModal('save'),
            children: saveModalBody,
            customStyles: updatePipelineStyles.modalStyles,
        };
    };

    const handleAddStepCloseModal = (): void => {
        setHasOpenAddStepModal(false);
    };

    const handleSubmitStepButton = (formValues: AddStepFormData): void => {
        setIsChangesUnsaved(true);
        if (formValues?.step?.id) {
            const currentSteps = getValues('stepIds') || [];
            const updatedSteps = [...currentSteps, formValues.step.id];
            setValue('stepIds', updatedSteps);
        }
        handleStepTableData();
        handleAddStepCloseModal();
    };

    const handlePermissionDialogClose = (): void => {
        setDisplayPermissionDialog(false);
    };

    const handleDeletePipeline = (): void => {
        deleteDataPipeline(
            { id: id as string, token: getToken() },
            {
                onSuccess: () => {
                    setIsChangesUnsaved(false);
                    handleOpenModal('delete');
                    setTimeout(() => {
                        navigate(PIPELINES_AND_STEPS_PATHS.LANDING_PAGE);
                    }, 500);
                },
            }
        );
    };

    const handleConfirmationModalConfig = (): ConfirmationModalConfig => {
        return {
            confirmationModal: openDeleteConfirmationModal,
            confirmationKey: data?.name ?? '',
            confirmationButtonText: 'Delete Pipeline',
            hasConfirmationTextField: true,
            confirmationCancelButtonText: 'Cancel',
            confirmationHeadingText: 'Confirm Pipeline Deletion',
            confirmationInputLabelText: `Type ${data?.name} below`,
            confirmationSubHeadingText: (
                <Typography>
                    By typing{' '}
                    <Typography
                        component='span'
                        sx={confirmationModalStyles.textBoldStyles}
                    >
                        ‘{data?.name}’
                    </Typography>{' '}
                    below, you understand that you are permanently deleting this
                    pipeline. This action{' '}
                    <Typography
                        component='span'
                        sx={confirmationModalStyles.textBoldStyles}
                    >
                        CANNOT
                    </Typography>{' '}
                    be undone.
                </Typography>
            ),
            handleConfirmation: () => handleDeletePipeline(),
            handleConfirmationModal: () => handleOpenModal('delete'),
        };
    };

    const handleTrialRunModalCloseButton = (): void => {
        setOpenTrialRunModal(null);
    };

    const handleSaveAndContinueButton = (): void => {
        handleSaveButtonClick('saveAndOpenTrialRun');
    };

    const handleCloseModal = (): void => {
        setOpenTrialRunModal(null);
    };

    if (!isDataPipelineServiceEnabled) {
        navigate(PIPELINES_AND_STEPS_PATHS.LANDING_PAGE);
        return <></>;
    }

    if (isPipelineLoading || isStepsLoading) {
        return <HelixLoader />;
    }

    return (
        <>
            <LandingPageWrapperWithLeftTabs
                includeBreadcrumbs={true}
                breadCrumLastItem={data?.name ?? ''}
                headingText={data?.name ?? ''}
                isLoading={isLoading}
                isChangesUnsaved={isChangesUnsaved}
                unsavedChangesConfirmationText={unsavedChangesConfirmationText}
                openCancelModal={openCancelModal}
                handleCancelButton={handleCancelButton}
                handleCancelModal={(): void => handleOpenModal('cancel')}
                handleCancelModalConfirmation={handleCancelModalConfirmation}
                handleSaveAndCloseButton={(): void =>
                    handleSaveButtonClick('saveAndClose')
                }
                handleSaveButton={(): void => handleSaveButtonClick('save')}
                saveModalConfig={
                    getValues('stepIds').length === 0
                        ? handleSaveModalConfig()
                        : undefined
                }
                isMenuComponentVisible={true}
                menuItems={menuItems}
                tabItems={tabNavItems}
                selectedTab={selectedTab}
                onTabChange={onTabChange}
                tabsContainerStyles={commonStyles.tabsContainerStyles}
                tabsStyles={commonStyles.tabsStyles}
                tabItemStyles={commonStyles.tabItemStyles}
                confirmationModalConfig={handleConfirmationModalConfig()}
            />
            {hasOpenAddStepModal && (
                <AddStepModal
                    open={true}
                    handleCloseButton={handleAddStepCloseModal}
                    handleSubmitStepButton={handleSubmitStepButton}
                />
            )}
            {openConfirmationRemoveStep && (
                <ConfirmationModal
                    confirmationModal={true}
                    hasConfirmationTextField={false}
                    handleConfirmationModal={handleCancelRemove}
                    handleConfirmation={handleConfirmRemove}
                    confirmationHeadingText='Confirm Step Removal'
                    confirmationSubHeadingText={
                        <Typography>
                            Are you sure you want to remove this step from the
                            pipeline?
                        </Typography>
                    }
                    confirmationButtonText='Remove Step'
                    confirmationCancelButtonText='Cancel'
                />
            )}
            {openTrialRunModal && (
                <TrialRunModal
                    open={openTrialRunModal}
                    handleCloseButton={handleTrialRunModalCloseButton}
                    trialRunId={id as string}
                    trialRunType='Pipeline'
                    trialRunPageHeading='Pipeline Trial Run'
                />
            )}
            {openTrialRunModal === false && (
                <CancelModal
                    cancelModal={!openTrialRunModal}
                    handleCancelModal={handleCloseModal}
                    cancelButtonText='Save and continue'
                    cancelHeadingText='You have unsaved changes.'
                    cancelSubHeadingText='This pipeline has unsaved changes. You must save changes before you can perform a trial run.'
                    cancelModalText='Go Back'
                    handleCancel={handleSaveAndContinueButton}
                />
            )}
            {displayPermissionDialog && (
                <InvalidPermissionsDialog
                    open={displayPermissionDialog}
                    onClose={handlePermissionDialogClose}
                />
            )}
        </>
    );
};
